import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "348",
  "slug": "countries-with-more-than-one-capital",
  "title": "Why Do Some Countries Have More Than One Capital City?",
  "excerpt": "South Africa has three. Bolivia argues about two. Here is why a single capital is not as universal as your classroom map suggested.",
  "date": "2026-06-04",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1499092346589-b9b6be3e94b2?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxjaXR5JTIwc2t5bGluZSUyMGFlcmlhbHxlbnwxfDB8fHwxNzgwNDc3MzU4fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Some countries split the branches of government across different cities — like South Africa's three capitals — to balance regional power, honor history, or keep the seat of government separate from the largest commercial city.",
  "content": [
    {
      "type": "paragraph",
      "text": "Most maps print a single star for each country's capital, which makes it feel like a settled fact. In practice the idea of 'the capital' is messier than the classroom version suggests, and a surprising number of countries deliberately spread the role across more than one city."
    },
    {
      "type": "heading",
      "text": "South Africa: Three Capitals"
    },
    {
      "type": "paragraph",
      "text": "The classic example is South Africa, which divides the branches of government across three cities. Pretoria is the executive capital and home to the presidency. Cape Town is the legislative capital, where parliament sits. Bloemfontein is the judicial capital, seat of the Supreme Court of Appeal. The arrangement dates to the country's formation in 1910, when the split was a compromise to share power among the former colonies and republics rather than handing everything to one city."
    },
    {
      "type": "heading",
      "text": "When the Capital Isn't the Biggest City"
    },
    {
      "type": "paragraph",
      "text": "Even with a single capital, plenty of countries keep the seat of government apart from their largest, busiest city on purpose. Brazil built Brasilia inland rather than crown Rio or Sao Paulo. Australia created Canberra as a neutral choice between rivals Sydney and Melbourne. The United States has Washington rather than New York, and Turkey runs from Ankara, not Istanbul. Separating political power from commercial gravity is a recurring theme, often meant to spread development or cool a regional rivalry."
    },
    {
      "type": "heading",
      "text": "Countries That Officially Split It"
    },
    {
      "type": "list",
      "items": [
        "Bolivia — Sucre is the constitutional and judicial capital, while La Paz is the seat of government.",
        "Netherlands — Amsterdam is the constitutional capital, but the government and royal residence sit in The Hague.",
        "Eswatini — Mbabane is the administrative capital and Lobamba the royal and legislative one.",
        "Sri Lanka — Sri Jayawardenepura Kotte is the legislative capital, with Colombo as the commercial and executive hub.",
        "Malaysia — Kuala Lumpur is the official capital, while the administrative government moved to purpose-built Putrajaya."
      ]
    },
    {
      "type": "heading",
      "text": "Official Versus Actual: The De Facto Capital"
    },
    {
      "type": "paragraph",
      "text": "There is a second kind of split that trips people up: when the official capital is not where the action really is. Several countries name one city on paper while the government largely operates from another. Tanzania's official capital is Dodoma, but much of national life long centered on Dar es Salaam. Cote d'Ivoire's official capital is Yamoussoukro, while Abidjan remains the economic heart. Benin lists Porto-Novo, but the government sits mostly in Cotonou. The label and the reality have drifted apart, and mapmakers have to choose which to print — which is why two atlases can disagree about the very same country's capital."
    },
    {
      "type": "heading",
      "text": "Capitals Built on Purpose"
    },
    {
      "type": "paragraph",
      "text": "A related twist is the planned capital — a city created or promoted specifically to be the seat of government, usually to break a deadlock or pull development inland. Brazil carved Brasilia out of the highlands in 1960. Australia invented Canberra to settle the Sydney-Melbourne rivalry. Kazakhstan moved its capital from Almaty to the northern city now called Astana in 1997, and Myanmar shifted its government to the newly built Naypyidaw in 2005. These cities can feel oddly quiet and over-scaled, because they were designed around ministries and monuments rather than centuries of organic growth — which is exactly why a 'capital' you have never heard of is often a purpose-built one."
    },
    {
      "type": "heading",
      "text": "Why Split a Capital at All?"
    },
    {
      "type": "paragraph",
      "text": "The reasons rhyme across these cases: balance rivalries between regions, separate the powers of government so no single city holds everything, honor an older historical capital, or simply relieve pressure on an overcrowded primary city. A divided capital is usually a political compromise made physical — and once the buildings exist, the arrangement tends to stick for a very long time. Moving a capital is so expensive and disruptive that even unpopular splits can outlast the politics that created them. The result is an atlas full of compromises frozen in place, long after anyone remembers the argument behind them."
    }
  ]
};
